import { createContext, useContext, useMemo } from 'react';

import type { OrderAppDeliveryMode } from '@/graphql/orderApp/api';
import type { CheckoutQuery } from '@/graphql/saleor/api';
import type { WithChildren } from '@/lib/types';
import type { ValidationRules } from '@/providers/ConfigProvider';

/**
 * Checkout data fetched during SSR (BEC-25061).
 *
 * Why a context and not `checkoutState`: `checkoutState` is a module-level
 * valtio proxy, i.e. one object shared by every request in the Node process.
 * Writing per-request data into it during a server render leaks it to other
 * customers. A context is created per render tree, so it is per-request by
 * construction and no leak is possible.
 *
 * This is read-only and only ever populated on the first render of a
 * server-rendered page. As soon as the client restore fills `checkoutState`,
 * the store wins - see `useCheckout`.
 */
export type CheckoutSsrValue = {
  /**
   * Exactly what `client.query<CheckoutQuery>(CheckoutDocument)` returns - the
   * same call the client's own checkoutRestore makes. Declaring it this way
   * rather than as CheckoutFragment is what makes prefetchDelivery's return
   * type and this type one definition instead of two that could drift.
   */
  checkout: NonNullable<CheckoutQuery['checkout']>;
  deliveryMethods: OrderAppDeliveryMode[];
  /**
   * Rules together with the country they were fetched for. The pair is
   * inseparable: rules for one country must never seed a form field that is
   * showing a different country, which is why the country travels with them
   * rather than being assumed to be the storefront locale (BEC-25061).
   */
  validationRules: { countryCode: string; rules: ValidationRules } | null;
};

const CheckoutSsrContext = createContext<CheckoutSsrValue | null>(null);

export const useCheckoutSsr = (): CheckoutSsrValue | null =>
  useContext(CheckoutSsrContext);

export const CheckoutSsrProvider = ({
  children,
  value,
}: WithChildren & { value?: CheckoutSsrValue | null }) => {
  // Memoised so consumers do not re-render when the parent re-renders with an
  // equivalent payload.
  const memoised = useMemo(() => value ?? null, [value]);

  return (
    <CheckoutSsrContext.Provider value={memoised}>
      {children}
    </CheckoutSsrContext.Provider>
  );
};
